import { FlightSearchRequest, FlightSearchResponse } from "@/types/flights";
import {
  useInfiniteQuery,
  UseInfiniteQueryResult,
  InfiniteData,
} from "@tanstack/react-query";
import api from "@/config/axiosConfig";

export const formatAirports = (airports: string | string[]) => {
  if (!airports) return "";
  if (typeof airports === "string") return airports;
  return airports.join(",");
};

interface PageParam {
  page: number;
  searchId?: string;
}

export const useFlightSearch = (
  request: FlightSearchRequest,
  enabled: boolean = true
): UseInfiniteQueryResult<
  InfiniteData<FlightSearchResponse, PageParam>,
  Error
> => {
  return useInfiniteQuery<
    FlightSearchResponse,
    Error,
    InfiniteData<FlightSearchResponse, PageParam>,
    (string | FlightSearchRequest)[],
    PageParam
  >({
    queryKey: ["flightSearch", request],
    queryFn: async ({ pageParam }) => {
      if (!pageParam.searchId) {
        // Initial search
        const response = await api.transport.post<FlightSearchResponse>(
          "/flights/search",
          {
            body: request,
          }
        );
        return {
          success: true,
          data: response.data.data,
        };
      } else {
        // Subsequent pages
        const response = await api.transport.get<FlightSearchResponse>(
          `/flights/pages?searchId=${pageParam.searchId}&page=${pageParam.page}&pageSize=20`
        );
        return {
          success: true,
          data: response.data.data,
        };
      }
    },
    initialPageParam: { page: 1, searchId: undefined },
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.data.currentPage < lastPage.data.totalPages) {
        return {
          page: lastPage.data.currentPage + 1,
          searchId: lastPage.data.searchId,
        };
      }
      return undefined;
    },
    enabled,
    staleTime: Infinity,
    retry: false,
  });
};
